import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import "./FilterDropdown.css";

/**
 * Reusable advanced filter dropdown with search.
 *
 * Props:
 *   label       - placeholder text when nothing selected
 *   value       - currently selected value (string)
 *   options     - array of strings or objects (e.g. [{id, name}])
 *   onChange    - callback(value)
 *   renderLabel - optional fn(option) => display string (for object options)
 *   getValue    - optional fn(option) => value string  (for object options, defaults to String(option) or option.id)
 */
const FilterDropdown = ({ label, value, options = [], onChange, renderLabel, getValue }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const getVal = (o) => getValue ? getValue(o) : (typeof o === 'object' ? String(o.id) : String(o));
    const getLbl = (o) => renderLabel ? renderLabel(o) : (typeof o === 'object' ? (o.name || o.label || String(o.id)) : String(o));

    const filtered = search
        ? options.filter(o => getLbl(o).toLowerCase().includes(search.toLowerCase()))
        : options;

    const selectedOption = value ? options.find(o => getVal(o) === String(value)) : null;
    const selectedLabel = selectedOption ? getLbl(selectedOption) : null;

    return (
        <div className="fd-drop" ref={ref}>
            <button
                className={`fd-drop__trigger ${value ? 'fd-drop__trigger--active' : ''}`}
                onClick={() => setOpen(!open)}
                type="button"
            >
                <span>{selectedLabel || label}</span>
                <FiChevronDown size={12} className={`fd-drop__chevron ${open ? 'fd-drop__chevron--open' : ''}`} />
            </button>
            {open && (
                <div className="fd-drop__panel">
                    <div className="fd-drop__search">
                        <FiSearch size={12} />
                        <input
                            type="text"
                            placeholder={`Search ${label.toLowerCase()}...`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="fd-drop__list">
                        <div
                            className={`fd-drop__item ${!value ? 'fd-drop__item--selected' : ''}`}
                            onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                        >
                            All {label}
                        </div>
                        {filtered.map(o => {
                            const val = getVal(o);
                            const lbl = getLbl(o);
                            const isSelected = String(value) === val;
                            return (
                                <div
                                    key={val}
                                    className={`fd-drop__item ${isSelected ? 'fd-drop__item--selected' : ''}`}
                                    onClick={() => { onChange(val); setOpen(false); setSearch(''); }}
                                >
                                    {lbl}
                                    {isSelected && <span className="fd-drop__check">✓</span>}
                                </div>
                            );
                        })}
                        {filtered.length === 0 && <div className="fd-drop__empty">No results</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
